import {AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {LegalHold} from '../../../../models/legal-hold';
import {LegalHoldService} from '../../../../services/legal-hold.service';
import {DialogService} from '../../../../services/dialog.service';
import {LegalHoldCreatorComponent} from '../creator/legal-hold-creator.component';
import {MatDialog} from '@angular/material/dialog';
import {LegalHoldReleaserComponent} from '../releaser/legal-hold-releaser.component';
import {UserService} from '../../../../services/user.service';
import {extractErrorMessage} from '../../../../utils/error-utils';
import {PolicyType} from '../../../../constants/retention-constants';

@Component({
  selector: 'app-legal-hold-list',
  templateUrl: './legal-hold-list.component.html',
  styleUrls: ['./legal-hold-list.component.scss']
})
export class LegalHoldListComponent implements OnInit, AfterViewInit, OnDestroy {

  private static readonly POLL_INTERVAL_MS = 5000;
  private pollingInterval: any;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public dataSource: MatTableDataSource<LegalHold>;
  public selectedHoldType: PolicyType = PolicyType.STANDARD;
  public columns = [
    {name: 'label', show: true},
    {name: 'created', show: true},
    {name: 'rules', show: true},
    {name: 'documents', show: true},
    {name: 'failed', show: true},
    {name: 'status', show: true},
    {name: 'edit', show: true}];
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(private holdService: LegalHoldService,
              private ngZone: NgZone,
              private userService: UserService,
              private dialog: MatDialog,
              private dialogService: DialogService) {
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource();
    this.list();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onHoldTypeTabChange(index: number): void {
    this.selectedHoldType = index === 0 ? PolicyType.STANDARD : PolicyType.FINRA;
    this.stopPolling();
    this.list();
  }

  create(): void {
    this.dialog.open(LegalHoldCreatorComponent, {
      height: '530px',
      width: '680px',
      data: {retentionType: this.selectedHoldType}
    }).afterClosed().subscribe((choice) => {
      if (choice) {
        this.list();
      }
    });
  }

  view(element: LegalHold): void {
    this.dialog.open(LegalHoldReleaserComponent, {
      height: '600px',
      width: '680px',
      data: element
    }).afterClosed().subscribe((choice) => {
      if (choice) {
        this.list();
      }
    });
  }

  list(showSpinner: boolean = true): void {
    if (showSpinner) {
      this.loadingSubject.next(true);
    }
    const type = this.isFinra() ? this.selectedHoldType : PolicyType.STANDARD;
    this.holdService.list(type).pipe(
      finalize(() => this.loadingSubject.next(false))
    ).subscribe({
      next: resp => {
        this.dataSource.data = resp;
        this.managePolling();
      },
      error: error => {
        this.stopPolling();
        this.dialogService.error(extractErrorMessage(error));
      }
    });
  }

  refresh(): void {
    this.list();
  }

  convertRules(rulesMap: any): string {
    let rules = '';
    if (rulesMap) {
      rules = this.holdService.convertRules(rulesMap);
    }
    return rules;
  }

  processedCount(legalHold: LegalHold): number {
    return legalHold.processedCount || 0;
  }

  failedCount(legalHold: LegalHold): number {
    return legalHold.failedCount || 0;
  }

  getStatusLabel(legalHold: LegalHold): string {
    switch (legalHold.status) {
      case 'APPLYING':
        return 'Apply in progress';
      case 'RELEASING':
        return 'Release in progress';
      case 'APPLIED':
        return 'Applied';
      case 'RELEASED':
        return 'Released';
      case 'FAILED':
        return 'Failed';
      case 'APPLIED_WITH_ERRORS':
        return 'Applied with errors';
      case 'RELEASED_WITH_ERRORS':
        return 'Released with errors';
      default:
        return '';
    }
  }

  isWithErrors(legalHold: LegalHold): boolean {
    return legalHold.status === 'APPLIED_WITH_ERRORS' || legalHold.status === 'RELEASED_WITH_ERRORS';
  }

  isInProgress(legalHold: LegalHold): boolean {
    return legalHold.status === 'APPLYING' || legalHold.status === 'RELEASING';
  }

  displayedColumns(): string[] {
    return this.columns.filter(column => column.show).map(column => column.name)
      .filter((value, index, self) => self.indexOf(value) === index);
  }

  isAuditor(): boolean {
    return this.userService.isAuditor();
  }

  isFinra(): boolean {
    return this.userService.isFinra();
  }

  private managePolling(): void {
    const hasInProgress = this.dataSource.data.some(h => this.isInProgress(h));
    if (hasInProgress && !this.pollingInterval) {
      this.pollingInterval = setInterval(() => this.list(false), LegalHoldListComponent.POLL_INTERVAL_MS);
    } else if (!hasInProgress) {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
}
